import { DATE_FORMAT, DISPLAY_TIME_FORMAT, TIME_FORMAT } from "../constants";
import moment from '../moment/moment';
import { filterManageableDayShifts, getActualShifts, resolveShiftCapacity } from "./shifts";

const INTERVAL_UNIT = 'minutes';

/**
 * Builds all time intervals for a shift.
 *
 * @param {Object} shift
 * @param {moment.Moment|string} selectedDay
 * @returns {Object[]}
 */
export function buildShiftIntervals(shift, selectedDay) {
    const shiftDate = moment(selectedDay).format(DATE_FORMAT);

    const shiftStart = moment(
        `${shiftDate} ${shift.from_hour}`,
        `${DATE_FORMAT} ${TIME_FORMAT}`
    );

    const shiftEnd = moment(
        `${shiftDate} ${shift.to_hour}`,
        `${DATE_FORMAT} ${TIME_FORMAT}`
    );

    const intervals = [];
    let currentStart = moment(shiftStart);

    while (currentStart.isBefore(shiftEnd)) {
        const currentEnd = moment(currentStart).add(
            shift.interval,
            INTERVAL_UNIT
        );

        intervals.push({
            start: moment(currentStart),
            end: moment.min(currentEnd, shiftEnd),
            label: currentStart.format(DISPLAY_TIME_FORMAT),
        });

        currentStart = currentEnd;
    }

    return intervals;
}

/**
 * Returns reservations starting inside a given interval.
 *
 * @param {Object[]} reservations
 * @param {Object} interval
 * @returns {Object[]}
 */
export function filterReservationsByInterval(reservations = [], interval) {
    return reservations.filter(reservation => {
        if (reservation.cancelled) {
            return false;
        }

        const reservationStart = moment(reservation.start_time);

        return reservationStart.isBetween(
            interval.start,
            interval.end,
            undefined,
            '[)'
        );
    });
}

/**
 * Returns the total guests count for reservations.
 *
 * @param {Object[]} reservations
 * @returns {number}
 */
export function countReservationsGuests(reservations = []) {
    return reservations.reduce((total, reservation) => {
        return total + reservation.adults + reservation.children;
    }, 0);
}

/**
 * Adds reservation statistics to shift intervals.
 *
 * @param {Object} shift
 * @param {moment.Moment|string} selectedDay
 * @param {Object[]} reservations
 * @returns {Object[]}
 */
export function buildShiftIntervalsWithReservations(shift, selectedDay, reservations = [], shiftCapacity) {
    const intervals = buildShiftIntervals(shift, selectedDay);
    const intervalCapacity = resolveIntervalCapacity(shift, shiftCapacity);
    const buckets = assignReservationsToIntervals(reservations, intervals);

    return intervals.map((interval, i) => {
        const intervalReservations = buckets[i];

        return {
            ...interval,
            reservations: intervalReservations,
            reservationsCount: intervalReservations.length,
            guestsCount: countReservationsGuests(intervalReservations),
            seatsLimit: intervalCapacity,
        };
    });
}

/**
 * Returns actual day shifts enriched with interval statistics.
 *
 * @param {moment.Moment|string} selectedDay
 * @param {Object[]} shifts
 * @param {Object[]} reservations
 * @returns {Object[]}
 */
export function buildShiftsWithIntervals(
    selectedDay,
    shifts = [],
    reservations = [],
    tables = []
) {
    return shifts.map(shift => {
        const shiftCapacity = resolveShiftCapacity(shift, tables);

        const intervals = buildShiftIntervalsWithReservations(
            shift,
            selectedDay,
            reservations,
            shiftCapacity
        );

        const shiftReservations = intervals.flatMap(
            interval => interval.reservations
        );

        return {
            ...shift,
            shiftCapacity,
            intervals,
            reservations: shiftReservations,
            guestsCount: countReservationsGuests(shiftReservations),
        };
    });
}
export function buildDayShiftsWithIntervals(
    selectedDay,
    shifts = [],
    reservations = [],
    tables = []
) {
    const actualShifts = getActualShifts(selectedDay, shifts);

    return buildShiftsWithIntervals(selectedDay, actualShifts, reservations, tables);
}

export function buildManageableDayShiftsWithIntervals(
    selectedDay,
    shifts = [],
    reservations = [],
    tables = []
) {
    const actualShifts = getActualShifts(selectedDay, shifts);
    const manageableShifts = filterManageableDayShifts(selectedDay, actualShifts);

    return buildShiftsWithIntervals(selectedDay, manageableShifts, reservations, tables);
}

export function resolveIntervalCapacity(shift, shiftCapacity) {
    if (shift.interval_limit_active) {
        return shift.interval_seats_limit;
    }

    return shiftCapacity;
}

// Module-level cache: maps a reservation object to its parsed start timestamp,
// without mutating the reservation itself. Entries are garbage-collected
// automatically once the reservation object is no longer referenced elsewhere.
const startMsCache = new WeakMap();

function getReservationStartMs(reservation) {
    let startMs = startMsCache.get(reservation);

    if (startMs === undefined) {
        startMs = moment(reservation.start_time).valueOf();
        startMsCache.set(reservation, startMs);
    }

    return startMs;
}

/**
 * Assigns reservations to their matching time interval using a single
 * linear merge pass, since both `reservations` and `intervals` are sorted
 * and intervals are contiguous (no gaps, no overlap).
 *
 * Complexity: O(reservations + intervals) instead of O(reservations * intervals).
 *
 * @param {Object[]} reservations - must be sorted by start_time ascending
 * @param {Object[]} intervals - contiguous, sorted intervals ({ start, end })
 * @returns {Object[][]} one bucket of reservations per interval
 */
export function assignReservationsToIntervals(reservations = [], intervals = []) {
    const buckets = intervals.map(() => []);

    if (intervals.length === 0) {
        return buckets;
    }

    let reservationIndex = 0;

    for (let i = 0; i < intervals.length; i++) {
        const { start, end } = intervals[i];
        const startMs = start.valueOf();
        const endMs = end.valueOf();

        while (reservationIndex < reservations.length) {
            const reservation = reservations[reservationIndex];

            // Skip cancelled reservations entirely, regardless of their time.
            if (reservation.cancelled) {
                reservationIndex++;
                continue;
            }

            // Cached lookup avoids re-parsing with moment() across shifts/intervals
            // that reuse the same reservation within the same render.
            const reservationMs = getReservationStartMs(reservation);

            // Reservation starts before the current interval (e.g. it
            // belongs to an earlier shift). It can never match any interval
            // of the current shift, so drop it and move on.
            if (reservationMs < startMs) {
                reservationIndex++;
                continue;
            }

            // Reservation belongs to a later interval: stop consuming here,
            // the outer loop will retry it against the next interval.
            if (reservationMs >= endMs) {
                break;
            }

            // Reservation falls within [start, end): assign it.
            buckets[i].push(reservation);
            reservationIndex++;
        }
    }

    return buckets;
}


// WEBPACK FOOTER //
// ./src/helpers/shiftsIntervals.js